import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Pagination } from 'app/shared/pagination';
import { SystemService } from 'app/system/system.service';

@Component({
	selector: 'app-product-results',
	templateUrl: './product-results.component.html',
	styleUrls: ['./product-results.component.scss']
})
export class ProductResultsComponent {

	@Input() items: any[] = [];
	@Input() pagination: Pagination = null;
	@Input() query = '';
	@Input() canAdd = false;
	@Input() canOpenDetail = false;
	// Whether a supplier's line opens the product editor. Off unless the host has somewhere to put that modal - the
	// picker is opened from inside the editor itself, so it cannot host one without nesting the two forever.
	@Input() canEditProduct = false;
	@Input() addedIds: any[] = [];
	@Input() addedProducts: any[] = [];
	@Input() emptyText = 'No products found.';
	@Input() addLabel = 'Add';
	@Input() addedLabel = 'Added';

	@Output() add: EventEmitter<any> = new EventEmitter();
	@Output() openProduct: EventEmitter<number> = new EventEmitter();
	@Output() editProduct: EventEmitter<number> = new EventEmitter();

	constructor(public systemService: SystemService) { }

	/**
	 * Expanded state lives in SystemService, keyed by the group id, so a group left open stays open while the
	 * caller pages through or searches again.
	 */
	isExpanded(item) {
		return this.systemService.isSystemExpanded(item.id);
	}

	toggleExpanded(item) {
		this.systemService.toggleSystemExpanded(item.id);
	}

	/**
	 * Discontinued is held per distributor copy, so a result row can be partly discontinued. Only a group with no
	 * copy left to buy is called discontinued outright - anything less says how many, because the product is still
	 * available from someone and the row shouldn't read as dead.
	 */
	allDiscontinued(item) {
		return !!item.distributor_count && item.discontinued_count === item.distributor_count;
	}

	someDiscontinued(item) {
		return item.discontinued_count > 0 && !this.allDiscontinued(item);
	}

	/**
	 * Whether any copy behind the row carries accessories or labour, which is what decides the row's height: the
	 * counts sit under the supplier's name, and the columns beside it have to grow by the same amount to keep each
	 * price and stock level against the supplier it belongs to.
	 */
	hasExtras(item) {
		return (item.distributors || []).some(distributor => distributor.accessories_count || distributor.labour_count);
	}

	/**
	 * Stock is null for a product the organisation doesn't stock at all, which is a different answer from holding
	 * none of it: the badge says "N/A" and stays neutral rather than reading as a shortage.
	 */
	stockKnown(stock) {
		return stock !== null && stock !== undefined;
	}

	stockClass(stock) {
		if (!this.stockKnown(stock)) return 'badge-lightergrey text-secondary';
		return stock > 0 ? 'badge-success' : 'badge-danger';
	}

	// Either way of saying it: a caller that only tracks which copies went on, or one that counts them.
	isAdded(distributor) {
		return (this.addedIds || []).indexOf(distributor?.product_id) !== -1 || this.addedQuantity(distributor) > 0;
	}

	// A collapsed group doesn't show its copies, so the row itself has to say something was taken from it.
	isRowAdded(item) {
		return (item.distributors || []).some(distributor => this.isAdded(distributor));
	}

	/**
	 * How many of the copy went on, where the caller counts them - 0 where it only knows that something did, and
	 * the row falls back to a tick. Counted per distributor copy, which is what was added: a group can have copies
	 * taken from more than one supplier.
	 */
	addedQuantity(distributor) {
		const added = (this.addedProducts || []).find(item => item.id === distributor?.product_id);
		return added?.quantity || 0;
	}

	addDistributor(distributor) {
		this.add.emit(distributor);
	}

	selectProduct(id) {
		if (this.canOpenDetail) this.openProduct.emit(id);
	}

	/**
	 * A supplier's line stands for one product record, so clicking it edits that copy rather than the group the row
	 * is named after - which is the only way to reach the copy a distributor actually supplies.
	 */
	openEditProduct(distributor, event) {
		if (!this.canEditProduct || !distributor?.product_id) return;

		// The Add button and the expand caret sit inside the same row; neither should end up opening the editor.
		if (event) event.stopPropagation();

		this.editProduct.emit(distributor.product_id);
	}

}
